import {IHttpService, IPromise, IQService} from 'angular';
import HttpService from '../http.service';
import ListeningPackageService from './listeningpackage.service';

import {
  FamilyPlanTypes,
  ListeningPackage,
  ListeningPackageAvailability,
  ListeningPackageType,
  PackageThumbnail,
  PriceDetails,
  PromocodeCalculateRequest,
  PromocodeCalculateResponse,
  PromocodeResponse,
  UserListeningPackage,
  UserListeningPackagesResponse
} from './package.model';
import PaycellPackageService from './paycellpackage.service';

export interface IPackageService {
  getPermAndGeneratePassword(
    packet: PackageThumbnail,
    paymentMethodId?: number
  ): IPromise<Response>;

  validatePasswordAndBuy(
    packet: PackageThumbnail,
    password: string,
    paymentMethodId?: number,
    cardId?: string
  ): IPromise<Response>;
}

/* @ngInject */
export default class PackageService implements IPackageService {
  private userConfig;
  private authPromise: IPromise<boolean>;

  public userOfferablePackages: PackageThumbnail[] = [];
  public priceDetails: PriceDetails;

  constructor(
    private $http: IHttpService,
    private $q: IQService,
    private httpService: HttpService,
    private listeningPackageService: ListeningPackageService,
    private paycellPackageService: PaycellPackageService,
    private $sce,
    private config
  ) {
  }

  public getPermAndGeneratePassword(
    packet: PackageThumbnail,
    paymentMethodId?: number
  ): IPromise<Response> {
    return this.getService(packet, paymentMethodId).then(service => {
      return service.getPermAndGeneratePassword(packet, paymentMethodId);
    });
  }

  public validatePasswordAndBuy(
    packet: PackageThumbnail,
    password: string,
    paymentMethodId?: number,
    cardId?: string
  ): IPromise<Response> {
    return this.getService(packet, paymentMethodId, cardId).then(service => {
      return service.validatePasswordAndBuy(packet, password, paymentMethodId, cardId);
    });
  }

  public getUserListeningPackagesNoCache(): IPromise<UserListeningPackagesResponse> {
    return this.httpService.extractResponseResult(this.$http.get(`hafifmuzik/mobile/listeningpackage/list/v2`));
  }

  public getUserListeningPackages(): IPromise<UserListeningPackagesResponse> {
    return this.httpService.getResultFromCache('listeningPackage', 'hafifmuzik/mobile/listeningpackage/list/v2');
  }

  public getUserOfferablePackages(): IPromise<PackageThumbnail[]> {
    if (this.userOfferablePackages.length > 0) {
      return this.$q.resolve(angular.copy(this.userOfferablePackages));
    } else {
      return this.$q
        .all([this.getUserListeningPackages()])
        .then(data => {
          const listeningPackagesInfo: UserListeningPackagesResponse = data[0];

          const offerableListeningPackages = listeningPackagesInfo.offerablePackages.map(
            offerablePackage => this.convertListeningPackage(offerablePackage)
          );

          const userListeningPackages = listeningPackagesInfo.userListeningPackages.map(
            userListeningPackage => this.convertListeningPackage(userListeningPackage.listeningPackage)
          );

          var onUser = false;
          offerableListeningPackages.forEach(offerable => {
            if (offerable.type === ListeningPackageType.CREDITCARD) {
              userListeningPackages.forEach(userListeningPackage => {
                if (userListeningPackage.id === offerable.id) {
                  onUser = true;
                  return;
                }
              });
              if (!onUser) {
                this.userOfferablePackages.push(offerable);
              }
              onUser = false;
            }
          });

          return angular.copy(this.userOfferablePackages);
        });
    }
  }

  public isPackageAvailable(
    id: number
  ): IPromise<ListeningPackageAvailability> {
    return this.httpService.extractResponseResult(
      this.$http.get(
        `hafifmuzik/mobile/listeningpackage/ispackageavailable/offerid/${id}`
      )
    );
  }

  public convertListeningPackage(
    listeningPackage: ListeningPackage
  ): PackageThumbnail {
    return {
      description: listeningPackage.description,
      id: listeningPackage.offerId,
      name: listeningPackage.name,
      smsKeyword: listeningPackage.smsKeyword,
      type: listeningPackage.type,
      price: listeningPackage.price,
      priceUnit: listeningPackage.priceUnit ? listeningPackage.priceUnit : 'TL',
      priceDescription: listeningPackage.priceDescription
        ? listeningPackage.priceDescription
        : '',
      relatedSDP: listeningPackage.relatedSdpOfferId,
      welcomePackage: listeningPackage.welcomePackage,
      radioPackage: listeningPackage.radioPackage,
      toggle: false,
      descHeader: this.getDescriptionHeader(listeningPackage.description, 0),
      descContent: this.getDescriptionHeader(listeningPackage.description, 1),
      isPremium: listeningPackage.premium,
      isPaid: listeningPackage.paid,
      icon: '',
      nameInTR: listeningPackage.nameInTR,
      period: listeningPackage.period,
      billStatus: listeningPackage.billStatus,
      isStudent: listeningPackage.student,
      family: listeningPackage.family,
      packageSegment: listeningPackage.packageSegment,
      sardisOfferId: listeningPackage.sardisOfferId,
      manageSubscription: listeningPackage.manageSubscription,
      changePaymentMethod: listeningPackage.changePaymentMethod,
      basePrice: listeningPackage.basePrice,
      trialPeriod: listeningPackage.trialPeriod,
      trialPeriodCount: listeningPackage.trialPeriodCount
    };
  }

  private getDescriptionHeader(description?: string, index?: number) {
    let result = undefined;
    if (!description) {
      return result;
    }

    const strMatch = description.match(/(<p>)?(\s)*----(\s)*(<\/p>)?/i);
    if (strMatch && strMatch[0]) {
      const texts = description.split(strMatch[0]);
      if (texts && texts.length >= 2) {
        result = this.$sce.getTrustedHtml(texts[index]);
      }
    }
    return result;
  }

  public getActiveListeningPackage(
    userListeningPackagesInfo: UserListeningPackagesResponse
  ): PackageThumbnail {
    const listeningPackages: UserListeningPackage[] =
      userListeningPackagesInfo.userListeningPackages;

    return listeningPackages && listeningPackages.length
      ? this.convertListeningPackage(listeningPackages[0].listeningPackage)
      : undefined;
  }

  public getActiveListeningPackages(
    userListeningPackagesInfo: UserListeningPackagesResponse
  ): PackageThumbnail[] {
    const listeningPackages: UserListeningPackage[] =
      userListeningPackagesInfo.userListeningPackages;

    const thumbnails: PackageThumbnail[] = [];
    if (listeningPackages && listeningPackages.length) {
      listeningPackages.forEach(listeningPackage => {
        thumbnails.push(
          this.convertListeningPackage(listeningPackage.listeningPackage)
        );
        thumbnails[thumbnails.length - 1].cancelReqSent =
          listeningPackage.cancelReqSent;
        thumbnails[thumbnails.length - 1].status =
          listeningPackage.status;
        thumbnails[thumbnails.length - 1].nextRenewalDate = listeningPackage.nextRenewalDate;
      });
    }

    return thumbnails;
  }

  private getService(
    packet: PackageThumbnail,
    paymentMethodId?: number,
    cardId?: string
  ): IPromise<IPackageService> {
    if (packet.type === ListeningPackageType.CREDITCARD && (!!paymentMethodId || !!cardId)) {
      return this.$q.resolve(this.paycellPackageService);
    } else if (!paymentMethodId || !cardId) {
      return this.$q.resolve(this.listeningPackageService);
    }
  }

  public getPackage() {
    return this.getUserListeningPackages().then(listeningPackagesInfo =>
      this.getActiveListeningPackages(listeningPackagesInfo)
    );
  }

  public getOfferablePackage() {
    return this.getUserListeningPackages().then(listeningPackagesInfo =>
      listeningPackagesInfo.offerablePackages.map(
        offerablePackage => this.convertListeningPackage(offerablePackage))
    );
  }

  public getPackageIcon(packageIcon: PackageThumbnail) {
    const premiumStudentList: string[] = this.config.showStudentPlanIcon;
    if (packageIcon.isStudent || (premiumStudentList && premiumStudentList.filter(element => element == packageIcon.id).length > 0)) {
      return 'icon-plans-student';
    } else if (packageIcon.family == FamilyPlanTypes.FAMILY) {
      return 'icon-family-plan';
    } else if (packageIcon.family == FamilyPlanTypes.DUO) {
      return 'icon-duo-plan';
    } else if (packageIcon.isPremium && this.checkIsCampaignPacket(packageIcon)) {
      return 'icon-plans-premium-campaign';
    } else if (packageIcon.isPremium) {
      return 'icon-plans-premium';
    } else {
      return 'icon-plans-starter';
    }
  }

  private checkIsCampaignPacket(isCampaignPacket: PackageThumbnail): boolean {
    if (isCampaignPacket.priceDescription) {
      this.priceDetails = JSON.parse(isCampaignPacket.priceDescription);
    }
    if (this.priceDetails) {
      if (
        (this.priceDetails.firstThreePrice && this.priceDetails.laterPrice && this.priceDetails.firstThreePrice !== this.priceDetails.laterPrice)
        ||
        (this.priceDetails.firstPrice && this.priceDetails.laterPrice && this.priceDetails.firstPrice !== this.priceDetails.laterPrice)
        ||
        (!this.priceDetails.firstThreePrice && !this.priceDetails.firstPrice && !this.priceDetails.laterPrice && this.priceDetails.totalPrice && this.priceDetails.pricePeriod === 'yearly')
        ||
        (!this.priceDetails.firstThreePrice && !this.priceDetails.firstPrice && this.priceDetails.firstXPrice && this.priceDetails.firstXFree && this.priceDetails.firstXMonth && this.priceDetails.laterPrice &&
          this.priceDetails.firstXPrice !== this.priceDetails.laterPrice)) {
        return true;
      }
    }
    return false;
  }

  public getUserActivePackagesByIds() {
    return this.$q
      .all([this.getPackage()]).then(data => {
        let listeningPackage = data[0];
        let offerIds = '';
        if (listeningPackage) {
          listeningPackage.forEach(lp => {
              if (offerIds.length > 0) {
                offerIds = offerIds.concat(', ');
              }
              ;
              offerIds = offerIds.concat(lp.id);
            }
          );
        }
        return offerIds;
      });
  }

  public checkRelated(
    packet: PackageThumbnail
  ): IPromise<ListeningPackage> {
    return this.httpService.extractResponseResult(
      this.$http.get(
        `hafifmuzik/mobile/listeningpackage/checkRelated/offerid/${packet.relatedSDP}`
      )
    );
  }

  public checkPromocode(
    promocode: string,
    sardisOfferId: string
  ): IPromise<PromocodeResponse> {
    return this.httpService.extractResponseResult(
      this.$http.get(
        `hafifmuzik/mobile/listeningpackage/checkPromocode?promocode=${promocode}&sardisOfferId=${sardisOfferId}`));
  }

  public calculatePromocode(
    promoCode: string,
    offerId: string
  ): IPromise<PromocodeCalculateResponse> {
    const request: PromocodeCalculateRequest = {
      offerId,
      promoCode
    };

    return this.httpService.extractResponseResult(
      this.$http.post(`hafifmuzik/mobile/listeningpackage/v2/checkPromocode`, request)
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/common/services/package/package.service.ts